import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { VideoSegment, getTimelinePercent, locateGlobalTime } from '../utils/segmentedVideo';
import { normalizeGrantedMediaUrl } from '../utils/mediaSources';
import GrantedPreviewImage from './GrantedPreviewImage';
import type { PreviewGrantProjection } from '../contexts/SearchWorkspaceContext';
import './SegmentedTimeline.css';

interface SegmentedTimelineProps {
  segments: VideoSegment[];
  globalTime: number;
  totalDuration: number;
  onChange: (time: number) => void;
  /** Whole-source playback can seek before annotations exist. */
  allowUnsegmentedSeeking?: boolean;
}

interface HoverState {
  xPercent: number;
  globalTime: number;
  localTime: number;
  segmentId: string;
}

export const SegmentedTimeline: React.FC<SegmentedTimelineProps> = ({
  segments,
  globalTime,
  totalDuration,
  onChange,
  allowUnsegmentedSeeking = false,
}) => {
  const [hover, setHover] = useState<HoverState | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ x: number; time: number } | null>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);

  // Format time for display
  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Calculate segment boundaries for visualization
  const segmentBoundaries = useMemo(() => {
    if (!Number.isFinite(totalDuration) || totalDuration <= 0) {
      return [];
    }
    return segments.map(segment => ({
      segment,
      leftPercent: (segment.start / totalDuration) * 100,
      widthPercent: ((segment.end - segment.start) / totalDuration) * 100
    }));
  }, [segments, totalDuration]);


  // Handle mouse move for hover preview
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!timelineRef.current || segments.length === 0) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    const hoverTime = ratio * totalDuration;

    // Find which segment this time belongs to
    const result = locateGlobalTime(hoverTime, segments);
    if (result) {
      const xPercent = (x / rect.width) * 100;

      setHover({
        xPercent,
        globalTime: hoverTime,
        localTime: result.localTime,
        segmentId: result.segment.id,
      });
    }

    // Handle dragging
    if (isDragging && dragStartRef.current) {
      const newTime = ratio * totalDuration;
      onChange(newTime);
    }
  }, [totalDuration, isDragging, onChange, segments]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    if (!isDragging) {
      setHover(null);
    }
  }, [isDragging]);

  // Handle drag start (also handles single click)
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (
      !timelineRef.current
      || (segments.length === 0 && !allowUnsegmentedSeeking)
      || !Number.isFinite(totalDuration)
      || totalDuration <= 0
    ) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    const time = ratio * totalDuration;

    // Immediately seek to clicked position
    onChange(time);

    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, time };

    // Keep the hover state to maintain preview
    // The preview should update naturally through the hover state

    // Add document listeners for drag
    const handleMouseMove = (e: MouseEvent) => {
      if (!timelineRef.current || !dragStartRef.current) return;

      const rect = timelineRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const ratio = Math.max(0, Math.min(1, x / rect.width));
      const newTime = ratio * totalDuration;
      onChange(newTime);
    };

    const cleanupDragListeners = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (dragCleanupRef.current === cleanupDragListeners) {
        dragCleanupRef.current = null;
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragStartRef.current = null;
      cleanupDragListeners();
    };

    dragCleanupRef.current?.();
    dragCleanupRef.current = cleanupDragListeners;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [totalDuration, onChange, segments.length, allowUnsegmentedSeeking]);

  useEffect(() => () => {
    dragCleanupRef.current?.();
    dragCleanupRef.current = null;
    dragStartRef.current = null;
  }, []);

  const playedPercent = getTimelinePercent(globalTime, totalDuration) * 100;
  const hoverSegment = hover
    ? segments.find((segment) => segment.id === hover.segmentId) || null
    : null;
  const hoverPosterUrl = normalizeGrantedMediaUrl(
    hoverSegment?.previewThumbnailUrl,
  ) || undefined;
  const hoverAnimatedUrl = normalizeGrantedMediaUrl(
    hoverSegment?.previewGifUrl,
  ) || undefined;
  const hoverProjection = useMemo<PreviewGrantProjection | undefined>(() => {
    if (
      !hoverSegment?.videoId
      || (!hoverSegment.previewThumbnailGcsUri && !hoverSegment.previewGifGcsUri)
    ) return undefined;
    return {
      identity: {
        kind: 'segment',
        video_id: hoverSegment.videoId,
        segment_id: hoverSegment.id,
      },
      thumbnailGcsUri: hoverSegment.previewThumbnailGcsUri || null,
      gifGcsUri: hoverSegment.previewGifGcsUri || null,
    };
  }, [hoverSegment]);

  return (
    <div className="segmented-timeline-container">
      {/* Hover preview box - Show video frame and timestamp */}
      {hover && segments.length > 0 && !isDragging && (
        <div
          className="timeline-hover-preview"
          style={{
            left: `${hover.xPercent}%`,
            transform: 'translateX(-50%)'
          }}
        >
          <div className="hover-preview-content">
            <GrantedPreviewImage
              posterUrl={hoverPosterUrl}
              animatedUrl={hoverAnimatedUrl}
              projection={hoverProjection}
              alt=""
              className="hover-preview-gif"
              containerClassName="granted-hover-preview"
              engaged
              placeholder={<div className="hover-preview-placeholder" />}
            />
          </div>
          <div className="hover-preview-time-only">
            {formatTime(hover.globalTime)}
          </div>
        </div>
      )}

      {/* Timeline bar */}
      <div
        ref={timelineRef}
        className={`timeline-bar-container ${isDragging ? 'dragging' : ''}`}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
      >
        <div className="timeline-bar">
          {/* Segment boundaries - visual segments */}
          {segmentBoundaries.map((boundary, index) => (
            <div
              key={boundary.segment.id}
              className="timeline-segment-boundary"
              style={{
                left: `${boundary.leftPercent}%`,
                width: `${boundary.widthPercent}%`,
                background: index % 2 === 0 ? 'rgba(255, 255, 255, 0.05)' : 'transparent'
              }}
              title={`Segment ${index + 1}`}
            />
          ))}

          {/* Played progress */}
          <div
            className="timeline-progress"
            style={{ width: `${playedPercent}%` }}
          />

          {/* Playhead */}
          <div
            className="timeline-playhead"
            style={{ left: `${playedPercent}%` }}
          />

          {/* Segment markers */}
          {segmentBoundaries.map((boundary, index) => {
            if (index === 0) return null; // Skip first marker
            return (
              <div
                key={`marker-${boundary.segment.id}`}
                className="timeline-segment-marker"
                style={{ left: `${boundary.leftPercent}%` }}
              />
            );
          })}
        </div>
      </div>

      {/* Time display only */}
      <div className="timeline-time-display">
        <span className="current-time">{formatTime(globalTime)}</span>
        <span className="time-separator">/</span>
        <span className="total-time">{formatTime(totalDuration)}</span>
      </div>

      {/* Segment info */}
      <div className="timeline-segment-info">
        <span className="segment-count">
          {segments.length} segments
        </span>
        {segments.length > 0 && (
          <span className="current-segment">
            Current: Segment {segments.findIndex(s => s.id === locateGlobalTime(globalTime, segments).segment.id) + 1}
          </span>
        )}
      </div>
    </div>
  );
};
